import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Plus, Trash2, ArrowLeft, Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('coach');
  const [hourlyRate, setHourlyRate] = useState('');
  const [variableRates, setVariableRates] = useState([]);
  const [showRatesStep, setShowRatesStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const { registerCoach, registerAthlete, registerDual } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Se è coach o both e non ha ancora impostato i compensi, mostra step compensi
    if ((role === 'coach' || role === 'both') && !showRatesStep) {
      setShowRatesStep(true);
      return;
    }
    
    setLoading(true);

    try {
      if (role === 'coach') {
        await registerCoach(name, email, password, parseFloat(hourlyRate) || 0, variableRates);
        toast.success('Registrazione completata!');
        window.location.href = '/lessons';
      } else if (role === 'athlete') {
        await registerAthlete(name, email, password);
        toast.success('Registrazione completata con successo!');
        window.location.href = '/athlete/dashboard';
      } else if (role === 'both') {
        await registerDual(name, email, password, parseFloat(hourlyRate) || 0, variableRates);
        toast.success('Registrazione completata! Puoi ora accedere sia come allenatore che come atleta.');
        window.location.href = '/lessons';
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore durante la registrazione'));
      setLoading(false);
    }
  };

  const addVariableRate = () => {
    setVariableRates([...variableRates, { min_athletes: '', max_athletes: '', rate_per_hour: '' }]);
  };

  const removeVariableRate = (index) => {
    setVariableRates(variableRates.filter((_, i) => i !== index));
  };

  const updateVariableRate = (index, field, value) => {
    const newRates = [...variableRates];
    newRates[index][field] = field === 'rate_per_hour' ? parseFloat(value) || 0 : parseInt(value) || 0;
    setVariableRates(newRates);
  };

  // Step 1: Dati personali e scelta ruolo
  const renderStep1 = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="name" className="text-zinc-300">Nome Completo</Label>
        <Input
          id="name"
          data-testid="register-name-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Mario Rossi"
          className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-zinc-300">Email</Label>
        <Input
          id="email"
          data-testid="register-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="il-tuo-email@esempio.it"
          className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" className="text-zinc-300">Password</Label>
        <div className="relative">
          <Input
            id="password"
            data-testid="register-password-input"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12 text-lg pr-10"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-zinc-300">Mi voglio registrare come:</Label>
        <RadioGroup value={role} onValueChange={setRole} className="space-y-3">
          <div className="flex items-center space-x-3 p-4 rounded-sm bg-zinc-900/50 border border-zinc-800 hover:border-[#ccff00]/30 transition-colors cursor-pointer">
            <RadioGroupItem value="coach" id="role-coach" className="border-zinc-700 text-[#ccff00]" />
            <Label htmlFor="role-coach" className="text-white font-medium cursor-pointer flex-1">
              Allenatore
              <span className="block text-sm text-zinc-400 mt-1">Gestisci lezioni, atleti e compensi</span>
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-sm bg-zinc-900/50 border border-zinc-800 hover:border-[#ccff00]/30 transition-colors cursor-pointer">
            <RadioGroupItem value="athlete" id="role-athlete" className="border-zinc-700 text-[#ccff00]" />
            <Label htmlFor="role-athlete" className="text-white font-medium cursor-pointer flex-1">
              Atleta
              <span className="block text-sm text-zinc-400 mt-1">Visualizza le tue lezioni e pagamenti</span>
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-sm bg-zinc-900/50 border border-zinc-800 hover:border-[#ccff00]/30 transition-colors cursor-pointer">
            <RadioGroupItem value="both" id="role-both" className="border-zinc-700 text-[#ccff00]" />
            <Label htmlFor="role-both" className="text-white font-medium cursor-pointer flex-1">
              Entrambi
              <span className="block text-sm text-zinc-400 mt-1">Accesso completo come allenatore e atleta</span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      <Button
        data-testid="register-submit-btn"
        type="submit"
        disabled={loading}
        className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-sm h-12 shadow-[0_0_15px_rgba(204,255,0,0.3)] hover:scale-[1.02] transition-all"
      >
        {loading ? 'Registrazione...' : (role === 'athlete' ? 'Registrati' : 'Continua')}
      </Button>

      <div className="text-center text-sm text-zinc-400">
        Hai già un account?{' '}
        <Link to="/login" className="text-[#ccff00] hover:underline font-medium">
          Accedi
        </Link>
      </div>
    </>
  );

  // Step 2: Compensi per allenatori
  const renderStep2 = () => (
    <>
      <button
        type="button"
        onClick={() => setShowRatesStep(false)}
        className="flex items-center text-zinc-400 hover:text-white transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Torna indietro
      </button>

      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-white mb-2">Imposta i tuoi Compensi</h2>
        <p className="text-zinc-400 text-sm">Questi dati potrai modificarli anche successivamente dal tuo profilo</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="hourlyRate" className="text-zinc-300">Compenso Orario Fisso (€)</Label>
        <Input
          id="hourlyRate"
          data-testid="register-hourly-rate-input"
          type="text"
          inputMode="decimal"
          value={hourlyRate}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || /^\d*\.?\d*$/.test(val)) {
              setHourlyRate(val);
            }
          }}
          placeholder="50.00"
          className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12 text-lg"
        />
        <p className="text-zinc-500 text-xs">La tua tariffa oraria di base</p>
      </div>

      <div className="space-y-4">
        <Label className="text-zinc-300">Tariffe variabili in base al numero di atleti in compresenza</Label>
        <p className="text-zinc-500 text-xs">Imposta tariffe diverse in base al numero di atleti presenti</p>
        
        {variableRates.length > 0 && (
          <div className="space-y-3">
            {variableRates.map((rate, index) => (
              <div key={index} className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm font-medium">Fascia {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeVariableRate(index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-zinc-400 text-xs">Min Atleti</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={rate.min_athletes}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*$/.test(val)) {
                          updateVariableRate(index, 'min_athletes', val);
                        }
                      }}
                      placeholder="1"
                      className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] h-10 text-sm rounded-lg"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Max Atleti</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={rate.max_athletes}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*$/.test(val)) {
                          updateVariableRate(index, 'max_athletes', val);
                        }
                      }}
                      placeholder="5"
                      className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] h-10 text-sm rounded-lg"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">€/Ora</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={rate.rate_per_hour}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          updateVariableRate(index, 'rate_per_hour', val);
                        }
                      }}
                      placeholder="40"
                      className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] h-10 text-sm rounded-lg"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          onClick={addVariableRate}
          className="w-full border-[#ccff00]/30 text-[#ccff00] hover:bg-[#ccff00]/10 rounded-xl"
        >
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Fascia Tariffa
        </Button>
      </div>

      <Button
        data-testid="register-final-submit-btn"
        type="submit"
        disabled={loading}
        className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-sm h-12 shadow-[0_0_15px_rgba(204,255,0,0.3)] hover:scale-[1.02] transition-all"
      >
        {loading ? 'Registrazione...' : 'Completa Registrazione'}
      </Button>
    </>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="font-black tracking-tight">
            <span className="block text-5xl sm:text-6xl lg:text-7xl text-[#ccff00]">
              Trainers'
            </span>
            <span className="block text-2xl sm:text-3xl lg:text-4xl text-white manager-word">
              MANAGER
            </span>
          </h1>
          <p className="text-zinc-400">
            {showRatesStep ? 'Step 2 di 2' : 'Crea il tuo account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="glass-card p-8 rounded-sm space-y-6">
          {showRatesStep ? renderStep2() : renderStep1()}
        </form>
      </div>
    </div>
  );
}
